import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useInbox } from "@/components/inbox/InboxProvider";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { ChatComposer } from "@/components/chat/ChatComposer";
import { Bot, CheckCheck, Hand, Trash2, PanelRight, Cpu, Ban } from "lucide-react";
import { useAuth } from "@/components/auth/AuthProvider";
import { AbroadTag, isAbroad } from "@/components/AbroadTag";
import { FUNNELS, funnelOf } from "@/components/chat/ChatProvider";
import { ChatSidePanel, sideCount } from "@/components/chat/ChatSidePanel";

export const ChatThread = ({ sid, onChanged, onDeleted, wide }) => {
  const [side, setSide] = useState(() => !!wide && window.innerWidth >= 1280);
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const last = useRef("");
  const box = useRef(null);
  const { openClient } = useInbox();

  const load = useCallback(async (reset) => {
    if (reset) { last.current = ""; setMsgs([]); setS(null); }
    try {
      const { data } = await api.get(`/chat/sessions/${sid}`, { params: { after: last.current } });
      setS(data.session);
      if (data.messages.length) {
        last.current = data.messages[data.messages.length - 1].at;
        setMsgs((p) => [...p, ...data.messages.filter((m) => !p.some((x) => x.id === m.id))]);
      }
    } catch { /* retry next tick */ }
  }, [sid]);

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 2500);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [msgs.length]);

  const attend = async () => {
    await api.post(`/chat/sessions/${sid}/attend`);
    toast.success("Conversa marcada como atendida");
    load(false); onChanged();
  };

  const remove = async () => {
    if (!window.confirm(`Apagar de vez a conversa de ${s.name}? Mensagens e anexos somem do CRM e isso não pode ser desfeito.`)) return;
    try { await api.delete(`/chat/sessions/${sid}`); toast.success("Conversa apagada"); onDeleted?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha ao apagar"); }
  };

  const block = async () => {
    if (s.blocked) {
      if (!window.confirm("Desbloquear esta pessoa? Ela volta a poder chamar no chat.")) return;
      try { await api.post(`/chat/sessions/${sid}/unblock`); toast.success("Desbloqueado"); load(false); onChanged(); }
      catch (e) { toast.error(e.response?.data?.detail || "Falha ao desbloquear"); }
      return;
    }
    if (!window.confirm("Bloquear esta pessoa? Ela não consegue mais chamar no chat (desta conversa e da internet de onde chamou) e as imagens que ela enviou são apagadas para sempre.")) return;
    try {
      const { data } = await api.post(`/chat/sessions/${sid}/block`);
      toast.success(`Bloqueado · ${data.files_wiped} imagem(ns) apagada(s)`); load(false); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao bloquear"); }
  };

  if (!s) return <div className="flex flex-1 items-center justify-center text-sm text-slate-500">Carregando conversa...</div>;
  const fn = FUNNELS[funnelOf(s)];
  const pendingMacs = !s.client_id ? new Set((s.mac_reads || []).filter((r) => r.mac).map((r) => r.mac)).size : 0;
  const chip = "rounded px-1.5 text-[10px] font-bold";
  return (
    <div data-testid="chat-thread" className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-[#1F293D] px-4 py-2.5">
        <div className="min-w-0 flex-1">
          <div data-testid="chat-thread-name" className="flex min-w-0 items-center gap-1.5 font-semibold text-white">
            <span className="truncate">{s.name}</span>
            {isAbroad(s.phone, s.abroad || s.client?.abroad) && <AbroadTag testid="chat-thread-abroad" />}
          </div>
          <div className="flex min-w-0 items-center gap-1 truncate text-[11px] text-slate-500">
            <span data-testid="chat-thread-funnel" className={`${chip} shrink-0 ${fn.cls}`}>{fn.label}</span>
            {s.img_flagged && <span data-testid="chat-thread-flagged" className={`${chip} shrink-0 bg-red-500 text-white`}>imagem imprópria</span>}
            {s.name_missing && <span data-testid="chat-thread-awaiting" className={`${chip} shrink-0 bg-amber-400/15 text-amber-300`}>aguardando dados</span>}
            <span className="truncate">
            <span className={s.phone ? "" : "text-amber-300"}>{s.phone || "sem WhatsApp ainda"}</span>
            {s.client ? <span className="text-emerald-300"> · cliente {s.client.panel_user} · vence {(s.client.due_date || "").split("-").reverse().join("/")}</span> : " · novo lead"}
            <span data-testid="chat-thread-app-state"> · {s.app_installed_at ? "app instalado" : "sem app"}{s.push ? " · avisos ativos" : ""}</span>
            {(s.fonte || s.app) && <span data-testid="chat-thread-source"> · {[s.fonte, s.app && `app ${s.app}`].filter(Boolean).join(" · ")}</span>}
            </span>
          </div>
        </div>
        {s.mode === "ai" && <span data-testid="chat-thread-ai" title="IA respondendo" className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 px-2 py-1 text-[11px] font-semibold text-violet-300"><Bot className="h-3.5 w-3.5" /><span className={`hidden ${wide ? "lg:inline" : ""}`}>IA</span></span>}
        {s.needs_human && s.status === "open" && <span title="Pediu atendente" className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-1 text-[11px] font-semibold text-red-300"><Hand className="h-3.5 w-3.5" /><span className={`hidden ${wide ? "lg:inline" : ""}`}>Atendente</span></span>}
        <button data-testid="chat-side-toggle" onClick={() => setSide((v) => !v)} title={side ? "Recolher detalhes" : "Ficha, MACs e apps"}
          className={`relative rounded-lg border p-2 transition-colors ${side ? "border-sky-500/50 bg-sky-500/10 text-sky-300" : "border-[#1F293D] text-slate-300 hover:bg-[#1A2234]"}`}>
          <PanelRight className="h-4 w-4" />
          {sideCount(s) > 0 && <span className="absolute -right-1.5 -top-1.5 rounded-full bg-pink-500 px-1 text-[10px] font-bold text-slate-950">{sideCount(s)}</span>}
        </button>
        {(!s.blocked || user?.role === "admin") && <button data-testid="chat-block-btn" onClick={block} title={s.blocked ? "Desbloquear (admin)" : "Bloquear pessoa"}
          className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-2 text-xs font-semibold transition-colors ${s.blocked ? "border-red-500 bg-red-500/15 text-red-200" : "border-red-500/30 text-red-300 hover:bg-red-500/10"}`}>
          <Ban className="h-4 w-4" /><span data-testid="chat-blocked-label">{s.blocked ? "Bloqueado" : "Bloquear"}</span></button>}
        {user?.role === "admin" && <button data-testid="chat-delete-btn" onClick={remove} title="Apagar conversa (admin)"
          className="rounded-lg border border-red-500/30 p-2 text-red-300 transition-colors hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>}
        {s.status === "open"
          ? <button data-testid="chat-attend-btn" onClick={attend} title="Marcar como atendido" className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/40 px-2.5 py-2 text-xs font-semibold text-emerald-300 transition-colors hover:bg-emerald-500/10"><CheckCheck className="h-3.5 w-3.5" /><span className={`hidden ${wide ? "lg:inline" : ""}`}>Atendido</span></button>
          : <span data-testid="chat-attended-label" className="hidden text-[11px] text-slate-500 sm:inline">Atendido por {s.attended_by}</span>}
      </div>
      {s.resumed_at && !s.verified && (
        <div data-testid="chat-resumed-warning" className="border-b border-amber-500/30 bg-amber-500/[0.08] px-4 py-1.5 text-[11px] text-amber-200">
          Conversa retomada em outro aparelho em {new Date(s.resumed_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}. Confirme o usuário de acesso antes de passar dados da conta ou PIX.
        </div>
      )}
      {(pendingMacs > 0 || s.mac_alert) && !side && (
        <button data-testid="chat-mac-pending-alert" onClick={() => setSide(true)}
          className="flex items-center gap-2 border-b border-pink-500/30 bg-pink-500/[0.08] px-4 py-1.5 text-left text-[11px] font-semibold text-pink-200 transition-colors hover:bg-pink-500/15">
          <Cpu className="h-3.5 w-3.5" /> {s.mac_alert ? `Bia: ${s.mac_alert}` : `${pendingMacs} MAC${pendingMacs > 1 ? "s" : ""} novo${pendingMacs > 1 ? "s" : ""} aguardando busca nos painéis`} <span className="ml-auto text-pink-300">ver →</span>
        </button>
      )}
      <div className="relative flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          <div ref={box} data-testid="chat-messages" className="flex-1 space-y-2 overflow-y-auto bg-[#0B0F17] px-4 py-4">
            {msgs.map((m) => <ChatMessage key={m.id} m={m} />)}
            {!msgs.length && <div className="py-10 text-center text-xs text-slate-500">O visitante ainda não escreveu.</div>}
          </div>
          {s.visitor_typing && <div data-testid="chat-visitor-typing" className="bg-[#0B0F17] px-4 pb-2 text-xs text-slate-500">{s.name.split(" ")[0]} está digitando...</div>}
          <ChatComposer session={s} onSent={() => { load(false); onChanged(); }} />
        </div>
        <ChatSidePanel s={s} open={side} wide={wide} onClose={() => setSide(false)} reload={() => load(false)}
          onLinked={() => { load(false); onChanged(); }} openClient={openClient} />
      </div>
    </div>
  );
};
